import { NgStyle } from '@angular/common';
import { Component, computed, effect, inject, Injector, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormControl, FormGroup } from '@angular/forms';
import { LocalStorageKeys, LocalStorageService } from '@iot-platform/core';
import { DateIntervalUtils, type DateRangeElement } from '@iot-platform/iot-platform-utils';
import { AbstractFormFieldComponent } from '../../abstract-form-field.component';
import { FormControlType } from '../../form-control-type.model';
import { DateTimepickerFormField, FormField } from '../../form-field.model';
import { FormDatePickerFieldComponent } from '../form-date-picker-field/form-date-picker-field.component';
import { FormDropdownFieldComponent } from '../form-dropdown-field/form-dropdown-field.component';

interface DateRangeForm {
  date: FormControl<Date | null>;
  hours: FormControl<number>;
  minutes: FormControl<number>;
}

export const DATE_TIME_PICKER_CHILD_CSS_CLASS_NAME = 'iot-platform-ui-form-field__date-time-picker--child';

@Component({
  selector: 'iot-platform-ui-form-date-time-picker-field',
  imports: [FlexLayoutModule, FormDatePickerFieldComponent, FormDropdownFieldComponent, NgStyle],
  template: `
    <div class="iot-platform-ui-form-field__date-time-picker" data-cy="iot-platform-ui-form-date-time-picker-field">
      @if (dateField(); as dateField) {
        <iot-platform-ui-form-date-picker-field
          [class]="dateField.cssClassName?.()"
          [field]="dateField"
          [fxFlex.lt-md]="dateField.fxFlex?.() ? '0 1 calc(50% - 10px)' : 'calc(50% - 10px)'"
          [fxFlex]="dateField.fxFlex?.() ? '0 1 calc(' + $safeNavigationMigration(dateField.fxFlex?.()) + ' - 10px)' : '1 1 0'"
          [ngStyle]="{ width: dateField.fxFlex?.() ? 'calc(' + $safeNavigationMigration(dateField.fxFlex?.()) + ' - 10px)' : '100%' }"
          [ngStyle.lt-md]="{ width: dateField.fxFlex?.() ? 'calc(50% - 10px)' : 'calc(50% - 10px)' }"
          fxFlex.lt-sm="100%"
          style="margin-right: 10px; place-content: center;"
          [field]="dateField"
          [control]="dateControl"
        />
      }

      @if (hoursField(); as hoursField) {
        <iot-platform-ui-form-dropdown-field
          [class]="hoursField.cssClassName?.()"
          [field]="hoursField"
          [fxFlex.lt-md]="hoursField.fxFlex?.() ? '0 1 calc(50% - 10px)' : 'calc(50% - 10px)'"
          [fxFlex]="hoursField.fxFlex?.() ? '0 1 calc(' + $safeNavigationMigration(hoursField.fxFlex?.()) + ' - 10px)' : '1 1 0'"
          [ngStyle]="{ width: hoursField.fxFlex?.() ? 'calc(' + $safeNavigationMigration(hoursField.fxFlex?.()) + ' - 10px)' : '100%' }"
          [ngStyle.lt-md]="{ width: hoursField.fxFlex?.() ? 'calc(50% - 10px)' : 'calc(50% - 10px)' }"
          fxFlex.lt-sm="100%"
          style="margin-right: 10px; place-content: center;"
          [field]="hoursField"
          [control]="hoursControl"
        />
      }

      @if (minutesField(); as minutesField) {
        <iot-platform-ui-form-dropdown-field
          [class]="minutesField.cssClassName?.()"
          [field]="minutesField"
          [fxFlex.lt-md]="minutesField.fxFlex?.() ? '0 1 calc(50% - 10px)' : 'calc(50% - 10px)'"
          [fxFlex]="minutesField.fxFlex?.() ? '0 1 calc(' + $safeNavigationMigration(minutesField.fxFlex?.()) + ' - 10px)' : '1 1 0'"
          [ngStyle]="{ width: minutesField.fxFlex?.() ? 'calc(' + $safeNavigationMigration(minutesField.fxFlex?.()) + ' - 10px)' : '100%' }"
          [ngStyle.lt-md]="{ width: minutesField.fxFlex?.() ? 'calc(50% - 10px)' : 'calc(50% - 10px)' }"
          fxFlex.lt-sm="100%"
          style="margin-right: 10px; place-content: center;"
          [field]="minutesField"
          [control]="minutesControl"
        />
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../../form-field/form-field.component.scss'
})
export class FormDateTimePickerFieldComponent extends AbstractFormFieldComponent<DateTimepickerFormField> implements OnInit {
  readonly storage = inject(LocalStorageService);
  readonly injector = inject(Injector);

  form!: FormGroup<DateRangeForm>;

  readonly hours: readonly number[] = DateIntervalUtils.getArrayOfConsecutiveNumbers(0, 23);
  readonly minutes: readonly number[] = DateIntervalUtils.getArrayOfConsecutiveNumbers(0, 59);

  readonly dateField: WritableSignal<Partial<FormField | null>> = signal(null);
  readonly hoursField: WritableSignal<Partial<FormField | null>> = signal(null);
  readonly minutesField: WritableSignal<Partial<FormField | null>> = signal(null);
  initialDate: WritableSignal<Date | null> = signal(null);
  private readonly DEFAULT_START_TIME = 0;
  initialHours = signal(this.DEFAULT_START_TIME);
  initialMinutes = signal(this.DEFAULT_START_TIME);
  initialSeconds = signal(this.DEFAULT_START_TIME);

  get dateControl(): FormControl<Date | null> {
    return this.form.controls.date;
  }

  get hoursControl(): FormControl<number> {
    return this.form.controls.hours;
  }

  get minutesControl(): FormControl<number> {
    return this.form.controls.minutes;
  }

  get timeZone(): string {
    return DateIntervalUtils.getTimezone(this.storage, LocalStorageKeys.STORAGE_BUSINESS_PROFILE_KEY);
  }

  ngOnInit(): void {
    this.initializeForm();
    this.initializeFormFields();
    super.ngOnInit();
  }

  onSelectionChange(event: any): void {
    const value = this.getDateValue();
    this.control()?.setValue(value);
    this.field()?.selectionChange?.({ ...event, value });
  }

  initializeFormFields(): void {
    this.dateField.set({
      ...this.field(),
      type: FormControlType.DATE_PICKER,
      name: signal('date'),
      fxFlex: signal('50%'),
      required: this.required,
      toggleIcon: this.field()?.toggleIcon,
      clearLabel: this.field()?.clearLabel,
      cssClassName: signal(`${DATE_TIME_PICKER_CHILD_CSS_CLASS_NAME} iot-platform-ui-form-field__date-time-picker_date`),
      valueChange: () => {
        const value = this.getDateValue();
        this.control()?.setValue(value);
        this.field()?.valueChange?.(value);
      },
      selectionChange: (event: { value: Date | null }) => this.onSelectionChange(event),
      onClear: (event) => {
        this.resetForm();
        this.field()?.onClear?.(event);
      },
      initialValue: this.initialDate
    });
    this.hoursField.set({
      type: FormControlType.DROP_DOWN_LIST,
      name: signal('hours'),
      label: this.field()?.placeholderHours,
      disabled: this.field()?.disabled,
      items: computed(() => this.hours),
      fxFlex: signal('25%'),
      trackBy: (item: number) => item,
      displayBy: (item: number) => item,
      selectionChange: (event: any) => this.onSelectionChange(event),
      cssClassName: signal(`${DATE_TIME_PICKER_CHILD_CSS_CLASS_NAME} iot-platform-ui-form-field__date-time-picker_hours`),
      initialValue: this.initialHours
    });

    this.minutesField.set({
      type: FormControlType.DROP_DOWN_LIST,
      name: signal('minutes'),
      label: this.field()?.placeholderMinutes,
      disabled: this.field()?.disabled,
      items: computed(() => this.minutes),
      fxFlex: signal('25%'),
      trackBy: (item: number) => item,
      displayBy: (item: number) => item,
      selectionChange: (event: any) => this.onSelectionChange(event),
      cssClassName: signal(`${DATE_TIME_PICKER_CHILD_CSS_CLASS_NAME} iot-platform-ui-form-field__date-time-picker_minutes`),
      initialValue: this.initialMinutes
    });
  }

  resetForm(): void {
    this.form.reset(
      {
        date: null,
        hours: this.initialHours(),
        minutes: this.initialMinutes()
      },
      { emitEvent: true }
    );
    this.control()?.setValue(null);
    this.field()?.selectionChange?.({ value: null });
  }

  override initInitialValueEffect() {
    effect(
      () => {
        const field = this.field();
        if (field?.initialValue) {
          const initialValue = field.initialValue();
          const date = new Date(initialValue);
          this.initialDate.set(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
          this.initialHours.set(date.getHours());
          this.initialMinutes.set(date.getMinutes());
          this.initialSeconds.set(date.getSeconds());
          this.control()?.setValue(initialValue);
        }

        if (field?.initialHours) {
          this.initialHours.set(field.initialHours());
        }
        if (field?.initialMinutes) {
          this.initialMinutes.set(field.initialMinutes());
        }
        if (field?.initialSeconds) {
          this.initialSeconds.set(field.initialSeconds());
        }
      },
      { injector: this.injector }
    );
  }

  getDateValue() {
    const value = this.dateControl.getRawValue();
    if (!value) return null;
    return this.getDate(value, this.hoursControl.value, this.minutesControl.value, this.initialSeconds());
  }

  getDate(date: Date, hours: number, minutes: number, seconds: number): DateRangeElement {
    return DateIntervalUtils.getDateRangeElement(date, hours, minutes, seconds, this.timeZone, this.field()?.dateFormatterPattern?.());
  }

  initializeForm(): void {
    this.form = new FormGroup<DateRangeForm>({
      date: new FormControl<Date | null>(null),
      hours: new FormControl<number>(this.initialHours(), { nonNullable: true }),
      minutes: new FormControl<number>(this.initialMinutes(), { nonNullable: true })
    });
  }
}
